import React, { memo, lazy, Suspense } from 'react';
import { Switch } from 'react-router-dom';
import PropTypes from 'prop-types';

import CommonLayout from 'components/layouts/CommonLayout';
import { ROUTES } from 'constants/routes';
import { TOOLS_LINKS } from 'constants/tools';
import DotsLoader from 'components/common/DotsLoader';
import { isMemberOf } from 'utils/user';
import { LS_USER_GROUPS } from 'constants/user';

const Dashboard = lazy(() => import('components/pages/Dashboard'));
const Media = lazy(() => import('components/tools/Media'));
const GeneratedMedia = lazy(() => import('components/tools/GeneratedMedia'));
const CampaignMediaUpload = lazy(
  () => import('components/tools/CampaignMediaUpload'),
);
const Campaigns = lazy(() => import('components/tools/Campaigns'));
const BookingsProblematic = lazy(
  () => import('components/tools/BookingsProblematic'),
);
const CampaignCreate = lazy(() => import('components/tools/CampaignCreate'));
const CampaignEdit = lazy(() => import('components/tools/CampaignEdit'));
const Upload = lazy(() => import('components/tools/Upload'));
const SlidesBuilder = lazy(() => import('components/tools/SlidesBuilder'));
const NotFound = lazy(() => import('components/pages/NotFound'));

const LOADING_FALLBACK = <DotsLoader backdrop />;

/**
 * Routes Component. Manages all application routes
 * Takes isAuthorized prop to manage public/private routes
 * @param { Boolean } isAuthorized
 */
function Routes({ user }) {
  const loggedIn = !!user?.email;

  return (
    <Suspense fallback={LOADING_FALLBACK}>
      <Switch>
        <CommonLayout
          exact
          path={ROUTES.dashboard}
          component={Dashboard}
          hasAccess={isMemberOf(user, LS_USER_GROUPS)}
        />
        <CommonLayout
          exact
          path={ROUTES.upload}
          component={Upload}
          toolLink={TOOLS_LINKS.upload}
          hasAccess={isMemberOf(user, TOOLS_LINKS.upload.allowedGroups)}
        />
        <CommonLayout
          exact
          fullWidth
          path={ROUTES.media}
          component={Media}
          toolLink={TOOLS_LINKS.media}
          hasAccess={isMemberOf(user, TOOLS_LINKS.media.allowedGroups)}
        />
        <CommonLayout
          exact
          fullWidth
          path={ROUTES.generatedMedia}
          component={GeneratedMedia}
          toolLink={TOOLS_LINKS.generatedMedia}
          hasAccess={isMemberOf(user, TOOLS_LINKS.generatedMedia.allowedGroups)}
        />
        <CommonLayout
          exact
          path={`${ROUTES.campaignMediaUpload}/:id`}
          component={CampaignMediaUpload}
          hasAccess={loggedIn}
          toolLink={TOOLS_LINKS.campaignMediaUpload}
        />
        <CommonLayout
          exact
          fullWidth
          path={ROUTES.campaigns}
          component={Campaigns}
          toolLink={TOOLS_LINKS.campaigns}
          hasAccess={isMemberOf(user, TOOLS_LINKS.campaigns.allowedGroups)}
        />
        <CommonLayout
          exact
          path={ROUTES.campaignCreate}
          component={CampaignCreate}
          toolLink={TOOLS_LINKS.campaignCreate}
          hasAccess={isMemberOf(user, TOOLS_LINKS.campaignCreate.allowedGroups)}
        />
        <CommonLayout
          exact
          path={`${ROUTES.campaigns}/:id`}
          component={CampaignEdit}
          toolLink={TOOLS_LINKS.campaignEdit}
          hasAccess={isMemberOf(user, TOOLS_LINKS.campaignEdit.allowedGroups)}
        />
        <CommonLayout
          exact
          fullWidth
          path={ROUTES.slides}
          toolLink={TOOLS_LINKS.slides}
          component={SlidesBuilder}
          hasAccess={isMemberOf(user, TOOLS_LINKS.slides.allowedGroups)}
        />
        <CommonLayout
          exact
          fullWidth
          path={ROUTES.bookingsProblematic}
          component={BookingsProblematic}
          toolLink={TOOLS_LINKS.bookingsProblematic}
          hasAccess={isMemberOf(
            user,
            TOOLS_LINKS.bookingsProblematic.allowedGroups,
          )}
        />
        <CommonLayout
          header="Page not found"
          component={NotFound}
          hasAccess={loggedIn}
        />
      </Switch>
    </Suspense>
  );
}

Routes.propTypes = {
  user: PropTypes.shape({
    email: PropTypes.string,
  }),
};

export default memo(Routes);
